import { useState, useCallback, useRef, useEffect } from 'react';

export interface VideoInteraction {
  videoId: string;
  action: 'play' | 'pause' | 'complete' | 'seek';
  timestamp: number;
  duration?: number;
}

export interface UseVideoPlayerReturn {
  currentVideo: string | null;
  playVideo: (videoId: string) => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  isPlaying: boolean;
  interactions: VideoInteraction[];
  clearInteractions: () => void;
}

export const useVideoPlayer = (): UseVideoPlayerReturn => {
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [interactions, setInteractions] = useState<VideoInteraction[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const trackInteraction = useCallback((interaction: Omit<VideoInteraction, 'timestamp'>) => {
    const fullInteraction: VideoInteraction = {
      ...interaction,
      timestamp: Date.now(),
    };

    setInteractions((prev) => [...prev, fullInteraction]);

    // Send analytics (in a real app, this would go to your analytics service)
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as { gtag?: (...args: unknown[]) => void }).gtag?.('event', 'video_interaction', {
        video_id: interaction.videoId,
        action: interaction.action,
        duration: interaction.duration,
      });
    }
  }, []);

  const playVideo = useCallback(
    (videoId: string) => {
      setCurrentVideo(videoId);
      setIsPlaying(true);
      trackInteraction({ videoId, action: 'play' });
    },
    [trackInteraction],
  );

  const pauseVideo = useCallback(() => {
    if (currentVideo) {
      setIsPlaying(false);
      trackInteraction({ videoId: currentVideo, action: 'pause' });
    }
  }, [currentVideo, trackInteraction]);

  const stopVideo = useCallback(() => {
    if (currentVideo) {
      setIsPlaying(false);
      trackInteraction({ videoId: currentVideo, action: 'complete' });
      setCurrentVideo(null);
    }
  }, [currentVideo, trackInteraction]);

  const clearInteractions = useCallback(() => {
    setInteractions([]);
  }, []);

  // Handle video element events
  useEffect(() => {
    const handleVideoEnd = () => {
      if (currentVideo) {
        trackInteraction({ videoId: currentVideo, action: 'complete' });
        setIsPlaying(false);
      }
    };

    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener('ended', handleVideoEnd);
      return () => {
        videoElement.removeEventListener('ended', handleVideoEnd);
      };
    }
  }, [currentVideo, trackInteraction]);

  return {
    currentVideo,
    playVideo,
    pauseVideo,
    stopVideo,
    isPlaying,
    interactions,
    clearInteractions,
  };
};
